import React, { useEffect, useState, useCallback } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Wallet, TrendingUp, RotateCcw, Users } from "lucide-react";

const COMMISSION_STATUSES = ["queued", "sent", "delivered", "opened", "clicked", "registered", "verified", "submitted", "bounced", "expired", "revoked", "unsubscribed"];

const statusColor = (s) => ({
  queued: "bg-slate-100 text-slate-700",
  sent: "bg-sky-100 text-sky-800",
  delivered: "bg-sky-100 text-sky-800",
  opened: "bg-violet-100 text-violet-800",
  clicked: "bg-violet-100 text-violet-800",
  registered: "bg-emerald-100 text-emerald-800",
  verified: "bg-emerald-200 text-emerald-900",
  submitted: "bg-emerald-600 text-white",
  bounced: "bg-amber-100 text-amber-900",
  expired: "bg-slate-200 text-slate-600",
  revoked: "bg-rose-100 text-rose-800",
  unsubscribed: "bg-rose-100 text-rose-800",
}[s] || "bg-slate-100 text-slate-700");

const entryColor = (t) => ({
  earn: "bg-emerald-100 text-emerald-800",
  reverse: "bg-rose-100 text-rose-800",
  payout: "bg-amber-100 text-amber-900",
}[t] || "bg-slate-100 text-slate-700");

/**
 * A19-P4 — QA Tester / QA Manager commission dashboard.
 * Path: /admin/qa-commissions
 *
 * Reads aggregated data from /api/admin/qa-commissions:
 *   • 4 KPI cards (earned / reversed / balance / invitations)
 *   • 3 breakdown cards (by country / profile / status)
 *   • Filters: country, profile, status
 *   • Invitations table
 *   • Last 50 ledger entries table
 */
const QaCommissions = () => {
  const { t, tCountry, tProfile } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ country: "", profile: "", status: "" });
  const [countries, setCountries] = useState([]);
  const [profiles, setProfiles] = useState([]);

  useEffect(() => {
    api.get("/profiles").then((r) => setProfiles(r.data || [])).catch(() => setProfiles([]));
    api.get("/countries").then((r) => setCountries(r.data || [])).catch(() => setCountries([]));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
      q.set("limit", "200");
      const res = await api.get(`/admin/qa-commissions?${q.toString()}`);
      setData(res.data);
    } catch (err) {
      toast.error(extractErrorMessage(err, "load failed"));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-6" data-testid="qa-commissions-page">
      <div className="flex items-center gap-3">
        <Wallet className="w-6 h-6 text-emerald-700" />
        <h1 className="text-2xl font-bold text-slate-900" data-testid="qa-commissions-title">
          {t("referrals.qa.heading")}
        </h1>
      </div>

      {/* KPI cards */}
      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="qa-commissions-kpis">
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-emerald-700 uppercase flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> {t("referrals.qa.totalEarned")}
              </div>
              <div className="text-3xl font-bold text-emerald-800" data-testid="qa-commissions-kpi-earned">{data.kpi.total_earned}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-rose-700 uppercase flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> {t("referrals.qa.totalReversed")}
              </div>
              <div className="text-3xl font-bold text-rose-800" data-testid="qa-commissions-kpi-reversed">{data.kpi.total_reversed}</div>
            </CardContent>
          </Card>
          <Card className="border-emerald-300 border-2">
            <CardContent className="p-4">
              <div className="text-xs text-emerald-800 uppercase font-bold flex items-center gap-1">
                <Wallet className="w-3 h-3" /> {t("referrals.qa.balance")}
              </div>
              <div className="text-3xl font-bold text-emerald-900" data-testid="qa-commissions-kpi-balance">{data.kpi.balance}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-slate-600 uppercase flex items-center gap-1">
                <Users className="w-3 h-3" /> {t("referrals.qa.invitations")}
              </div>
              <div className="text-3xl font-bold text-slate-800" data-testid="qa-commissions-kpi-invitations">{data.kpi.invitations_count}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader><CardTitle className="text-sm">{t("referrals.admin.filters")}</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-4 gap-2">
          <select
            value={filters.country}
            onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
            className="border rounded-md px-2 py-1.5 text-sm"
            data-testid="qa-commissions-filter-country"
          >
            <option value="">{`— ${t("referrals.admin.filterCountry")} —`}</option>
            {countries.map((c) => (
              <option key={c.code} value={c.code}>{`${c.code} — ${tCountry(c.code, c)}`}</option>
            ))}
          </select>
          <select
            value={filters.profile}
            onChange={(e) => setFilters((f) => ({ ...f, profile: e.target.value }))}
            className="border rounded-md px-2 py-1.5 text-sm"
            data-testid="qa-commissions-filter-profile"
          >
            <option value="">{`— ${t("referrals.admin.filterProfile")} —`}</option>
            {profiles.map((p) => (
              <option key={p.key} value={p.key}>{tProfile(p.key, p)}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className="border rounded-md px-2 py-1.5 text-sm"
            data-testid="qa-commissions-filter-status"
          >
            <option value="">{`— ${t("referrals.admin.filterStatus")} —`}</option>
            {COMMISSION_STATUSES.map((s) => (
              <option key={s} value={s}>{t(`referrals.status.${s}`)}</option>
            ))}
          </select>
          <Button onClick={fetchData} disabled={loading} data-testid="qa-commissions-apply">
            {loading ? "…" : t("referrals.admin.apply")}
          </Button>
        </CardContent>
      </Card>

      {/* Breakdowns */}
      {data && (
        <div className="grid lg:grid-cols-3 gap-3">
          {["by_country", "by_profile", "by_status"].map((k) => (
            <Card key={k}>
              <CardHeader><CardTitle className="text-sm">{t(`referrals.qa.${k}`)}</CardTitle></CardHeader>
              <CardContent>
                {Object.keys(data.breakdowns[k]).length === 0 ? (
                  <p className="text-slate-400 text-xs">—</p>
                ) : (
                  <ul className="space-y-1 text-sm" data-testid={`qa-commissions-breakdown-${k}`}>
                    {Object.entries(data.breakdowns[k]).map(([key, count]) => (
                      <li key={key} className="flex justify-between border-b border-slate-100 pb-1">
                        <span className="font-mono text-xs">{key}</span>
                        <span className="font-bold">{count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Invitations table */}
      <Card>
        <CardHeader><CardTitle className="text-sm">{t("referrals.qa.referralsHeading")}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto p-0">
          {!data || data.invitations.length === 0 ? (
            <p className="p-6 text-slate-500 text-sm" data-testid="qa-commissions-invitations-empty">
              {t("referrals.qa.referralsEmpty")}
            </p>
          ) : (
            <table className="min-w-full text-sm" data-testid="qa-commissions-invitations-table">
              <thead>
                <tr className="text-start text-xs uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-3">{t("referrals.list.colTarget")}</th>
                  <th className="py-2 px-3">{t("referrals.list.colProfile")}</th>
                  <th className="py-2 px-3">{t("referrals.list.colLang")}</th>
                  <th className="py-2 px-3">{t("referrals.list.colStatus")}</th>
                  <th className="py-2 px-3">{t("referrals.list.colSent")}</th>
                </tr>
              </thead>
              <tbody>
                {data.invitations.map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-100" data-testid={`qa-commissions-inv-${inv.id}`}>
                    <td className="py-2 px-3 font-mono text-xs">{inv.target_email_masked}</td>
                    <td className="py-2 px-3">{inv.target_profile_hint || "—"}</td>
                    <td className="py-2 px-3 uppercase">{inv.target_language_hint || "—"}</td>
                    <td className="py-2 px-3"><Badge className={statusColor(inv.status)}>{t(`referrals.status.${inv.status}`)}</Badge></td>
                    <td className="py-2 px-3 text-slate-500 text-xs">
                      {inv.sent_at ? new Date(inv.sent_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Ledger table */}
      <Card>
        <CardHeader><CardTitle className="text-sm">{t("referrals.qa.ledgerHeading")}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto p-0">
          {!data || data.ledger.length === 0 ? (
            <p className="p-6 text-slate-500 text-sm" data-testid="qa-commissions-ledger-empty">
              {t("referrals.qa.ledgerEmpty")}
            </p>
          ) : (
            <table className="min-w-full text-sm" data-testid="qa-commissions-ledger-table">
              <thead>
                <tr className="text-start text-xs uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-3">{t("referrals.qa.colTs")}</th>
                  <th className="py-2 px-3">{t("referrals.qa.colType")}</th>
                  <th className="py-2 px-3 text-end">{t("referrals.qa.colPoints")}</th>
                  <th className="py-2 px-3">{t("referrals.qa.colEvent")}</th>
                  <th className="py-2 px-3">{t("referrals.qa.colInvitation")}</th>
                </tr>
              </thead>
              <tbody>
                {data.ledger.map((e) => (
                  <tr key={e.id} className="border-b border-slate-100" data-testid={`qa-commissions-ledger-row-${e.id}`}>
                    <td className="py-2 px-3 text-slate-500 text-xs font-mono">{e.ts?.slice(0, 19).replace("T", " ") || "—"}</td>
                    <td className="py-2 px-3"><Badge className={entryColor(e.entry_type)}>{e.entry_type}</Badge></td>
                    <td className={`py-2 px-3 text-end font-bold ${e.points > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {e.points > 0 ? `+${e.points}` : e.points}
                    </td>
                    <td className="py-2 px-3 text-xs">{e.ref_event_type || "—"}</td>
                    <td className="py-2 px-3 font-mono text-xs text-slate-400">{(e.ref_invitation_id || "").slice(0, 8) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QaCommissions;
